import { Component, OnInit } from '@angular/core';
import { StateService } from '@uirouter/angular';

import { WikiService } from '../../../../widgets/wiki/services/wiki.service';
import { UserService } from '../../../services/core.user.service';
import { UploadService } from '../../../services/core.upload.service';
import { BreadcrumbService } from '../../../../widgets/breadcrumbs/breadcrumbs.service';
import { NotebookHost, NotebookUploadCallbacks } from '../../../../widgets/notebook/notebook-host';
import { marker } from '../../../services/ngx-translate-extract-marker';

/**
 * Personal "User Notebook" — a per-user, multi-page, versioned markdown space.
 *
 * A thin host wrapper around <gf-notebook>: it builds a {@link NotebookHost}
 */
@Component({
  selector: 'user-notebook',
  template: require('./user.notebook.component.html')
})
export class UserNotebookComponent implements OnInit {
  user: any;
  crumbs: any[] = [];
  host!: NotebookHost;

  constructor(
    private wikiService: WikiService,
    private userService: UserService,
    private uploadService: UploadService,
    private breadcrumbService: BreadcrumbService,
    private stateService: StateService
  ) {}

  ngOnInit(): void {
    this.user = this.userService.session.getUser();
    this.crumbs = [
      this.breadcrumbService.userMyPageCrumb(this.user),
      this.breadcrumbService.translatedCrumb(marker('Notebook'), 'home.user.notebook', { unixName: this.user.unixName })
    ];

    const self = this;
    this.host = {
      section: 'user',
      get refId() { return self.user.id; },
      canUpdate: true,                 // the route is same-user guarded
      enableVisibility: true,
      enableWikiLinks: true,
      enablePublicLinks: true,
      attachmentsPerPage: true,

      pageListParams: () => ({ user: self.user.id }),

      listAttachments: (page: any) => self.wikiService.getAttachments(page.id),
      uploadAttachments: (page: any, files: File[], callbacks: NotebookUploadCallbacks) =>
        self.uploadAttachments(page, files, callbacks),
      deleteAttachment: (page: any, file: any) => self.wikiService.deleteAttachment(page.id, file.id),

      publicPageHref: (page: any) => self.publicPageHref(page),
    };
  }

  private uploadAttachments(page: any, files: File[], callbacks: NotebookUploadCallbacks): Promise<any> {
    const options = {
      // refId is the wiki page id; setAttachment claims the chunk for the page's latest version.
      successCallback: (_section: string, refId: number, file: any, hash: any) =>
        this.wikiService.setAttachment(refId, hash, {
          fileName: file.name,
          fileType: file.type || 'application/octet-stream'
        }),
      progressCallback: callbacks.progressCallback,
      finishedCallback: callbacks.finishedCallback
    };
    return this.uploadService.uploadFiles('wikifiles', page.id, files, options);
  }

  private publicPageHref(page: any): string {
    if (!page || !page.pagename) { return ''; }
    return this.stateService.href('home.user.notebookpage',
      { unixName: this.user.unixName, pagename: page.pagename }, { absolute: true }) || '';
  }
}
